import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { Row, Col, Panel, Label } from 'react-bootstrap';
import request from 'axios';
import Moment from 'moment';
import Button from './common/Button';
import TextInput from './common/TextInput';
import LoadingProgressBar from './common/LoadingProgressBar';
import { caseSetServerError } from '../actions/caseActions';
import { isAdmin } from '../helpers/utils';
import { getClientAuthHeader } from '../../client/token';


const DAY_FORMAT = 'YYYY-MM-DD';

class AdminErrors extends PureComponent {
    constructor(props) {
        super(props);
        this.loadErrors = this.loadErrors.bind(this);
        this.state = {
            errors: undefined,
            users: undefined,
            loading: false,
            // Default range: last week.
            from: Moment.utc().subtract(7, 'days').format(DAY_FORMAT),
            to: Moment.utc().format(DAY_FORMAT),
            userId: '',
            textFilter: '',
            expanded: {},
        };
    }

    componentDidMount() {
        this.loadUsers();
        this.loadErrors();
    }

    async loadUsers() {
        try {
            const result = await request.get('/api/v1/admin/users', getClientAuthHeader());
            this.setState({ users: result.data.users });
        } catch (error) {
            console.error(new Error().stack, error);
        }
    }

    async loadErrors() {
        this.setState({ loading: true, expanded: {} });
        try {
            const from = Moment.utc(this.state.from, DAY_FORMAT).startOf('day').toISOString();
            const to = Moment.utc(this.state.to, DAY_FORMAT).endOf('day').toISOString();
            const userId = this.state.userId ? `&userId=${this.state.userId}` : '';
            const result = await request.get(
                `/api/v1/admin/errors?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}${userId}`,
                getClientAuthHeader());

            this.setState({
                errors: result.data.errors,
                truncated: result.data.truncated,
                total: result.data.total,
                loading: false,
            });
        } catch (error) {
            console.error(new Error().stack, error);
            this.props.dispatch(caseSetServerError('Loading errors failed.'));
            this.setState({ loading: false });
        }
    }

    render() {
        const { loggedIn } = this.props;
        if (loggedIn && !isAdmin(loggedIn)) {
            return (<h5 style={{ margin: 20 }}>Access denied.</h5>);
        }

        const users = this.state.users || [];
        const textFilter = this.state.textFilter.trim().toLowerCase();
        let errors = this.state.errors || [];
        if (textFilter) {
            errors = errors.filter(e =>
                `${e.Message || ''} ${e.UserEmail || ''} ${e.Request || ''}`.toLowerCase().indexOf(textFilter) !== -1);
        }

        const rows = [];
        errors.forEach((error, i) => {
            const expanded = this.state.expanded[i];
            const firstLine = (error.Message || '').split('\n')[0];
            const count = error.Count || 1;
            const seenTitle = count > 1
                ? `${count} occurrences from ${Moment.utc(error.FirstSeen).format('M/D/YYYY HH:mm:ss')} to ${Moment.utc(error.LastSeen || error.Time).format('M/D/YYYY HH:mm:ss')} UTC`
                : Moment.utc(error.Time).format('M/D/YYYY HH:mm:ss UTC');
            rows.push(
                <tr key={i}
                    style={{ cursor: 'pointer' }}
                    onClick={() => this.setState({ expanded: { ...this.state.expanded, [i]: !expanded } })}>
                    <td style={{ whiteSpace: 'nowrap' }} title={seenTitle}>
                        {Moment.utc(error.Time).fromNow()}
                    </td>
                    <td style={{ whiteSpace: 'nowrap', textAlign: 'center' }}
                        title={count > 1 && error.UserCount ? `${error.UserCount} distinct user(s)` : undefined}>
                        <Label bsStyle={count > 1 ? 'warning' : 'default'}>{count}</Label>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                        {error.UserEmail || <span style={{ color: '#bbb' }}>—</span>}
                        {error.UserId ? <span style={{ color: '#888' }}> ({error.UserId})</span> : null}
                        {count > 1 && error.UserCount > 1
                            ? <span style={{ color: '#888' }}> +{error.UserCount - 1} more</span>
                            : null}
                    </td>
                    <td>
                        <Label bsStyle={error.Source === 'client' ? 'info' : 'default'}>{error.Source}</Label>
                    </td>
                    <td style={{ maxWidth: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {firstLine.slice(0, 200)}
                    </td>
                </tr>
            );

            if (expanded) {
                rows.push(
                    <tr key={`${i}-detail`}>
                        <td colSpan={5} style={{ background: '#f9f9f9' }}>
                            {error.Request && (<div style={{ marginBottom: 6, color: '#666' }}>{error.Request}</div>)}
                            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 400, overflowY: 'auto' }}>
                                {error.Message}
                            </pre>
                        </td>
                    </tr>
                );
            }
        });

        return (
            <div style={{ margin: '0 20px' }}>
                <Row>
                    <Col xs={12}>
                        <Panel data-hj-whitelist header={`Errors${this.state.errors ? ` (${errors.length} unique${this.state.total && this.state.total !== errors.length ? `, ${this.state.total} total` : ''})` : ''}`}>
                            <Row style={{ marginBottom: 10 }}>
                                <Col xs={6} md={2}>
                                    <input type="date"
                                           className="form-control"
                                           value={this.state.from}
                                           onChange={(evt) => this.setState({ from: evt.target.value })} />
                                </Col>
                                <Col xs={6} md={2}>
                                    <input type="date"
                                           className="form-control"
                                           value={this.state.to}
                                           onChange={(evt) => this.setState({ to: evt.target.value })} />
                                </Col>
                                <Col xs={6} md={3}>
                                    <select className="form-control"
                                            value={this.state.userId}
                                            onChange={(evt) => this.setState({ userId: evt.target.value }, this.loadErrors)}>
                                        <option value="">All users</option>
                                        {users.slice().sort((a, b) => (a['Email'] || '').localeCompare(b['Email'] || ''))
                                              .map(u => (
                                                  <option key={u.id} value={u.id}>{u['Email']} ({u.id})</option>
                                              ))}
                                    </select>
                                </Col>
                                <Col xs={6} md={3}>
                                    <TextInput field={{
                                                   name: 'errorTextFilter',
                                                   value: this.state.textFilter,
                                                   onChange: (evt) => this.setState({ textFilter: evt.target.value }),
                                               }}
                                               placeholder="Filter message text"
                                    />
                                </Col>
                                <Col xs={12} md={2}>
                                    <Button bsStyle="primary"
                                            label="Apply"
                                            icon="refresh"
                                            disabled={this.state.loading}
                                            onClick={this.loadErrors} />
                                </Col>
                            </Row>
                            <LoadingProgressBar loading={this.state.loading || this.state.errors === undefined} />
                            {this.state.truncated && (
                                <p style={{ color: '#8a6d3b', background: '#fcf8e3', border: '1px solid #faebcc',
                                            borderRadius: 4, padding: '6px 10px', marginBottom: 10 }}>
                                    Showing the {errors.length} most recent distinct errors. More distinct errors
                                    exist in this range — narrow the date range or filter by user to see them.
                                </p>
                            )}
                            {this.state.errors && (
                            <div style={{ overflowX: 'auto' }}>
                                <table className="table table-hover" style={{ marginBottom: 0 }}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: 130 }}>Last seen</th>
                                            <th style={{ width: 60, textAlign: 'center' }}>Count</th>
                                            <th style={{ width: 220 }}>User</th>
                                            <th style={{ width: 70 }}>Source</th>
                                            <th>Error (click row for details)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows}
                                    </tbody>
                                </table>
                                {this.state.errors.length === 0 && (
                                    <p style={{ color: '#888', padding: 10 }}>No errors in the selected range. 🎉</p>
                                )}
                            </div>)}
                        </Panel>
                    </Col>
                </Row>
            </div>
        );
    }
}


const mapStateToProps = (state) => {
    return {
        loggedIn: state.user.loggedIn,
    };
};

export default connect(mapStateToProps)(AdminErrors);
